<template>
  <div class="tel-ui relative" v-on-clickaway="closeDropdown">
    <!-- Floating bell button (matches the other bottom-right FABs) -->
    <span
      v-if="count > 0"
      class="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-6 h-6 flex items-center justify-center rounded-full animate-bounce z-10"
    >
      {{ count > 9 ? '9+' : count }}
    </span>
    <button
      @click="toggleDropdown"
      class="w-12 h-12 md:w-14 md:h-14 rounded-full border-2 border-red-500 bg-white text-red-500 shadow-lg flex items-center justify-center hover:bg-red-50 transition-colors"
      :title="$t('mc_bell_title')"
    >
      <Icon class="text-3xl" icon="heroicons-outline:phone-arrow-down-left" />
    </button>

    <!-- Dropdown opens UPWARD (bottom-full) so it doesn't go off-screen. The OUTER
         div owns the absolute anchoring; an INNER div is the relative box that the
         loading overlay + the scroll area live in (mixing absolute+relative on one
         element breaks the anchoring). -->
    <div
      v-if="open"
      class="absolute bottom-full right-0 mb-3 w-[calc(100vw-1.5rem)] max-w-xs sm:w-80"
    >
      <div class="relative max-h-[70vh] sm:max-h-96 overflow-y-auto bg-white rounded-lg shadow-xl border border-slate-100">
      <div class="px-4 py-3 border-b flex items-center justify-between sticky top-0 bg-white z-10">
        <h4 class="text-sm font-semibold text-slate-800">{{ $t('mc_title') }}</h4>
        <span class="text-xs text-slate-400">{{ count }}</span>
      </div>

      <div v-if="count === 0" class="px-4 py-8 text-center text-sm text-slate-400">
        {{ $t('mc_empty') }}
      </div>

      <!-- While ANY item is being claimed, disable the whole list so the agent
           can't click a second order / another action mid-redirect. -->
      <ul v-else class="divide-y divide-slate-100" :class="{ 'pointer-events-none': !!claimingId }">
        <li
          v-for="mc in missedCalls"
          :key="mc._id"
          class="px-4 py-3 hover:bg-slate-50 cursor-pointer flex items-center gap-3 transition-opacity"
          :class="{ 'opacity-40': claimingId && claimingId !== mc._id, 'bg-primary/5': claimingId === mc._id }"
          @click="claim(mc)"
        >
          <div class="flex-shrink-0 h-9 w-9 rounded-full flex items-center justify-center"
               :class="claimingId === mc._id ? 'bg-primary/10 text-primary' : 'bg-red-50 text-red-500'">
            <Icon v-if="claimingId === mc._id" icon="heroicons-outline:arrow-path" class="text-lg animate-spin" />
            <Icon v-else icon="heroicons-outline:phone-arrow-down-left" class="text-lg" />
          </div>
          <div class="min-w-0 flex-1">
            <p class="text-sm font-medium text-slate-800 truncate">
              {{ mc.orderName || $t('mc_unknown_client') }}
            </p>
            <p class="text-xs text-slate-500 truncate" dir="ltr">
              {{ mc.phone }}
              <span v-if="mc.orderNo"> · #{{ mc.orderNo }}</span>
            </p>
          </div>
          <span class="text-[11px] font-semibold flex-shrink-0"
                :class="claimingId === mc._id ? 'text-primary animate-pulse' : 'text-primary'">
            {{ claimingId === mc._id ? $t('mc_claiming') : $t('mc_callback') }}
          </span>
        </li>
      </ul>

      <!-- Full-dropdown loading overlay during the claim+redirect (blocks input,
           tells the agent to wait — the redirect can take a moment). -->
      <div v-if="claimingId"
           class="absolute inset-0 bg-white/70 backdrop-blur-[1px] flex flex-col items-center justify-center gap-2 z-20 rounded-lg">
        <Icon icon="heroicons-outline:arrow-path" class="text-3xl text-primary animate-spin" />
        <p class="text-xs font-medium text-slate-600">{{ $t('mc_connecting') }}</p>
      </div>
      </div>
    </div>
  </div>
</template>

<script>
import { directive as onClickaway } from 'vue-clickaway';
export default {
  directives: { onClickaway },
  name: "MissedCallsFab",
  data() {
    return { open: false, claimingId: null };
  },
  computed: {
    missedCalls() {
      return this.$store.state.missedCalls || [];
    },
    count() {
      return this.missedCalls.length;
    },
  },
  methods: {
    toggleDropdown() {
      this.open = !this.open;
    },
    closeDropdown() {
      this.open = false;
    },
    // Claim a callback ATOMICALLY. Winner → redirect to /confirmation/:id.
    // Loser (already taken) → toast + drop it. See the missedCalls controller.
    async claim(mc) {
      if (!mc || this.claimingId) return;
      this.claimingId = mc._id;
      try {
        const res = await this.$server.customPost("missedCalls/claimMissedCall", { id: mc._id });
        const c = (res && res.content) || {};
        if (c.ok && c.order) {
          this.$store.commit("REMOVE_MISSED_CALL", mc._id);
          this.closeDropdown();
          this.$router.push(`/orders/confirmation/${c.order}`).catch(() => {});
        } else if (c.reason === "already_taken" || (res && res.status === 409)) {
          this.$store.commit("REMOVE_MISSED_CALL", mc._id);
          this.$toast ? this.$toast.info(this.$t("mc_already_taken")) : null;
        } else if (c.ok && !c.order) {
          this.$store.commit("REMOVE_MISSED_CALL", mc._id);
          this.$toast ? this.$toast.info(this.$t("mc_no_order")) : null;
        } else {
          this.$toast ? this.$toast.warning(this.$t("mc_claim_error")) : null;
        }
      } catch (e) {
        this.$toast ? this.$toast.warning(this.$t("mc_claim_error")) : null;
      } finally {
        this.claimingId = null;
      }
    },
  },
};
</script>
